import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { toast } from '../hooks/use-toast';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import MapDrawingTool from './MapDrawingTool';

const QuoteForm = ({ defaultConnectionType = 'gas', defaultService = '', title = 'Get a Quote' }) => {
  const [connectionType, setConnectionType] = useState(defaultConnectionType);
  const [mapImage, setMapImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    postcode: '',
    service: defaultService,
    connectionType: defaultConnectionType,
    capacity: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyConsent) {
      toast({
        title: "Privacy Consent Required",
        description: "Please agree to the privacy policy before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        mapImage: mapImage
      };
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setIsSubmitted(true);
        toast({
          title: "Quote Request Sent!",
          description: result.message || "We'll get back to you within 24 hours.",
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          postcode: '',
          service: defaultService,
          connectionType: defaultConnectionType,
          capacity: '',
          message: ''
        });
        setConnectionType(defaultConnectionType);
        setMapImage(null);
        setPrivacyConsent(false);
      } else {
        toast({
          title: "Error",
          description: result.detail || "Failed to send quote request. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send quote request. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleConnectionTypeChange = (value) => {
    setConnectionType(value);
    handleChange('connectionType', value);
    handleChange('capacity', '');
  };

  return (
    <section id="quote-form" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription>
              Fill out the form below and our team will get back to you within 24 hours with a quotation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="quote-name">Full Name *</Label>
                  <Input 
                    id="quote-name" 
                    required 
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote-email">Email Address *</Label>
                  <Input 
                    id="quote-email" 
                    type="email" 
                    required 
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="quote-phone">Phone Number *</Label>
                  <Input 
                    id="quote-phone" 
                    type="tel" 
                    required 
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="01675 488644"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote-company">Company Name</Label>
                  <Input 
                    id="quote-company" 
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Your Company Ltd"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-postcode">Site Postcode *</Label>
                <Input 
                  id="quote-postcode" 
                  required 
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value)}
                  placeholder="B77 5ES"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-service">Service Required</Label>
                <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gas-connections">Gas Connections</SelectItem>
                    <SelectItem value="water-connections">Water Connections</SelectItem>
                    <SelectItem value="electrical-connections">Electrical Connections</SelectItem>
                    <SelectItem value="design">Design Services</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="metering">Metering</SelectItem>
                    <SelectItem value="general">General Enquiry</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Connection Type *</Label>
                <RadioGroup value={connectionType} onValueChange={handleConnectionTypeChange}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="gas" id="quote-gas" />
                    <Label htmlFor="quote-gas" className="font-normal cursor-pointer">Gas Connection</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="water" id="quote-water" />
                    <Label htmlFor="quote-water" className="font-normal cursor-pointer">Water Connection</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="electrical" id="quote-electrical" />
                    <Label htmlFor="quote-electrical" className="font-normal cursor-pointer">Electrical Connection</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="multi" id="quote-multi" />
                    <Label htmlFor="quote-multi" className="font-normal cursor-pointer">Multi Utility Connection</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote-capacity">
                  {connectionType === 'gas' ? 'Required Capacity (cubic meters/hour)' : 'Required Capacity'}
                </Label>
                <Input 
                  id="quote-capacity" 
                  type="number"
                  step="0.01"
                  value={formData.capacity}
                  onChange={(e) => handleChange('capacity', e.target.value)}
                  placeholder={connectionType === 'gas' ? 'e.g., 100' : 
                               connectionType === 'electrical' ? 'e.g., 500' :
                               connectionType === 'water' ? 'e.g., 10' : 'e.g., 100'}
                />
                <p className="text-sm text-gray-500">
                  If known, please specify your required capacity. Leave blank if unsure.
                </p>
              </div>

              {/* Map Drawing Tool */}
              <MapDrawingTool onMapImageCapture={setMapImage} />

              <div className="space-y-2">
                <Label htmlFor="quote-message">Additional Details</Label>
                <Textarea 
                  id="quote-message" 
                  rows={6}
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Please provide any additional information about your project requirements, timeline, location, etc."
                />
              </div>

              {/* Privacy Policy Consent */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border">
                <Checkbox 
                  id="quote-privacy-consent" 
                  checked={privacyConsent}
                  onCheckedChange={setPrivacyConsent}
                  className="mt-0.5"
                  data-testid="quote-privacy-consent-checkbox"
                />
                <Label htmlFor="quote-privacy-consent" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                  By pressing submit you agree to the{' '}
                  <Link to="/privacy-policy" className="text-emerald-600 hover:text-emerald-700 underline" target="_blank">
                    privacy policy
                  </Link>
                  . *
                </Label>
              </div>

              {isSubmitted ? (
                <div className="w-full p-6 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                  <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-emerald-800 mb-2">Quote Request Sent Successfully!</h3>
                  <p className="text-emerald-700 mb-4">We&apos;ll get back to you within 24 hours.</p>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setIsSubmitted(false)}
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Submit Quote Request
                    </>
                  )}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default QuoteForm;
